import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  ButtonBase,
  CircularProgress,
  Drawer,
  InputAdornment,
  Paper,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import { useNavigate } from 'react-router-dom';
import colors from '../../assets/theme/colors';
import CareDocumentDetailSheet from './CareDocumentDetailSheet';
import { subscribeToCareDocumentSources } from './careDocumentSourceService';
import {
  getCareLibrarySourceCollectedCount,
  getCareLibrarySourceCollectedRows,
  getCareLibrarySourceSavedLabel,
  getCareLibrarySourceTitle,
  buildCareLibrarySubjectGroups,
  buildCareLibrarySubjectItemDestination,
  formatCareLibraryEvidenceCount,
  formatCareLibraryItemLastObserved,
} from './careLibraryDisplayModel';
import { filterLibrarySourcesByTab, isLibraryDocumentSource, isLibraryImageSource } from './careLibraryViewModel';
import ImportHistoryLibraryLink from '../importHistory/ImportHistoryLibraryLink';
import DesktopCareDocumentLibrary from './DesktopCareDocumentLibrary';
import MobileCareDocumentLibrary from './MobileCareDocumentLibrary';
import { navigateFromDocumentLibrary } from './documentLibraryNavigation';

const TABS = [
  { value: 'all', label: 'All' },
  { value: 'documents', label: 'Documents' },
  { value: 'images', label: 'Images' },
  { value: 'bySource', label: 'By source' },
  { value: 'bySubject', label: 'By subject' },
];

const normalizeText = (value = '') => String(value || '').replace(/\s+/g, ' ').trim();

const getSavedDateLabel = (source = {}) => getCareLibrarySourceSavedLabel(source);

const CareLibraryIcon = ({ source }) => {
  const isImage = isLibraryImageSource(source);
  const isDocument = isLibraryDocumentSource(source);
  const bgcolor = isImage ? colors.semantic.sageMist : colors.semantic.profileFlow.medsIconBg;
  const color = isImage ? colors.semantic.sageDeep : colors.semantic.profileFlow.medsIconInk;

  return (
    <Box
      sx={{
        width: 42,
        height: 42,
        borderRadius: '14px',
        display: 'grid',
        placeItems: 'center',
        bgcolor,
        color,
        flexShrink: 0,
      }}
    >
      <Inventory2OutlinedIcon sx={{ fontSize: isDocument ? 22 : 20 }} />
    </Box>
  );
};

const LibrarySourceItemRow = ({ item = {}, onOpen = () => {} }) => {
  const title = normalizeText(item.title || item.subtitle || '');
  const subtitle = normalizeText(item.subtitle || '');
  const canOpen = typeof onOpen === 'function';

  return (
    <ButtonBase
      onClick={canOpen ? onOpen : undefined}
      sx={{
        width: '100%',
        textAlign: 'left',
        borderRadius: '14px',
        px: 0.2,
        py: 0.3,
        color: colors.semantic.ink,
        justifyContent: 'flex-start',
        alignItems: 'flex-start',
        '&:hover': { bgcolor: alpha(colors.semantic.sageMist, 0.42) },
      }}
    >
      <Stack spacing={0.15} sx={{ width: '100%', minWidth: 0 }}>
        <Typography sx={{ fontSize: '0.94rem', fontWeight: 850, lineHeight: 1.3, color: colors.semantic.profileFlow.careStory.text }}>
          {title}
        </Typography>
        {subtitle ? (
          <Typography sx={{ fontSize: '0.8rem', fontWeight: 650, lineHeight: 1.3, color: alpha(colors.semantic.ink, 0.64) }}>
            {subtitle}
          </Typography>
        ) : null}
      </Stack>
      <ChevronRightRoundedIcon sx={{ mt: 0.15, ml: 0.8, flexShrink: 0, color: alpha(colors.semantic.ink, 0.34), fontSize: 22 }} />
    </ButtonBase>
  );
};

const LibrarySourceCard = ({ source = {}, onOpenSource = () => {}, onOpenItem = () => {} }) => {
  const title = getCareLibrarySourceTitle(source);
  const savedLabel = getSavedDateLabel(source);
  const collectedRows = getCareLibrarySourceCollectedRows(source);
  const collectedCount = getCareLibrarySourceCollectedCount(source);

  return (
    <Paper
      variant="outlined"
      sx={{
        borderRadius: '18px',
        boxShadow: 'none',
        borderColor: colors.semantic.line,
        bgcolor: '#FFFFFF',
        overflow: 'hidden',
      }}
    >
      <Box sx={{ px: { xs: 1.25, sm: 1.45 }, py: 1.05 }}>
        <Stack spacing={0.7}>
          <Box>
            {savedLabel ? (
              <Typography sx={{ fontSize: '0.78rem', fontWeight: 900, letterSpacing: '0.08em', textTransform: 'uppercase', color: alpha(colors.semantic.ink, 0.58) }}>
                {savedLabel}
              </Typography>
            ) : null}
            <Stack direction="row" spacing={0.75} alignItems="baseline" sx={{ mt: savedLabel ? 0.3 : 0, flexWrap: 'wrap' }}>
              <Typography sx={{ fontSize: '1.02rem', fontWeight: 930, color: colors.semantic.profileFlow.careStory.text }}>
                {title}
              </Typography>
              <Typography sx={{ fontSize: '0.82rem', fontWeight: 750, color: alpha(colors.semantic.ink, 0.58) }}>
                {`${collectedCount} thing${collectedCount === 1 ? '' : 's'} collected`}
              </Typography>
            </Stack>
          </Box>

          {collectedRows.length ? (
            <Stack spacing={0.6} sx={{ pt: 0.2 }}>
              {collectedRows.map((item) => (
                <LibrarySourceItemRow
                  key={item.id}
                  item={item}
                  onOpen={() => onOpenItem(item, source)}
                />
              ))}
            </Stack>
          ) : null}

          <Stack direction="row" spacing={1} alignItems="center" sx={{ pt: collectedRows.length ? 0.35 : 0 }}>
            <Button
              onClick={() => onOpenSource(source)}
              variant="text"
              sx={{
                px: 0,
                minWidth: 0,
                color: colors.semantic.sageDeep,
                fontSize: '0.94rem',
                fontWeight: 850,
                textTransform: 'none',
                '&:hover': { bgcolor: 'transparent', textDecoration: 'underline' },
              }}
            >
              View original source
            </Button>
          </Stack>
        </Stack>
      </Box>
    </Paper>
  );
};

const LibrarySubjectSummaryCard = ({ group = {}, onOpen = () => {} }) => (
  <ButtonBase
    onClick={onOpen}
    sx={{
      width: '100%',
      textAlign: 'left',
      borderRadius: '18px',
      border: `1px solid ${colors.semantic.line}`,
      bgcolor: '#FFFFFF',
      boxShadow: `0 4px 14px ${colors.landing.shadowSoft}`,
      px: { xs: 1.25, sm: 1.45 },
      py: 1.1,
      '&:hover': { bgcolor: alpha(colors.semantic.sageMist, 0.52) },
      '&:focus-visible': {
        outline: `2px solid ${colors.semantic.sageDeep}`,
        outlineOffset: 2,
      },
    }}
  >
    <Box sx={{ minWidth: 0, width: '100%' }}>
      <Stack direction="row" spacing={1} justifyContent="space-between" alignItems="baseline">
        <Typography sx={{ fontSize: '1.02rem', fontWeight: 920, color: colors.semantic.profileFlow.careStory.text }}>
          {group.label}
        </Typography>
        <Typography sx={{ fontSize: '0.96rem', fontWeight: 900, color: colors.semantic.sageDeep }}>
          {group.itemCount}
        </Typography>
      </Stack>
      <Typography sx={{ mt: 0.3, fontSize: '0.85rem', fontWeight: 650, color: alpha(colors.semantic.ink, 0.68), lineHeight: 1.35 }}>
        {group.description}
      </Typography>
      <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mt: 0.65, color: alpha(colors.semantic.ink, 0.56) }}>
        <Typography sx={{ fontSize: '0.79rem', fontWeight: 750 }}>
          {group.updatedLabel}
        </Typography>
        <ChevronRightRoundedIcon sx={{ fontSize: 20 }} />
      </Stack>
    </Box>
  </ButtonBase>
);

const LibrarySubjectItemRow = ({ item = {}, onOpen = () => {} }) => (
  <ButtonBase
    onClick={onOpen}
    sx={{
      width: '100%',
      textAlign: 'left',
      borderRadius: '16px',
      border: `1px solid ${alpha(colors.semantic.line, 0.9)}`,
      bgcolor: '#FFFFFF',
      px: 1.05,
      py: 0.9,
      '&:hover': { bgcolor: alpha(colors.semantic.sageMist, 0.5) },
    }}
  >
    <Box sx={{ minWidth: 0, width: '100%' }}>
      <Typography sx={{ fontSize: '0.98rem', fontWeight: 900, color: colors.semantic.profileFlow.careStory.text, lineHeight: 1.25 }}>
        {item.title}
      </Typography>
      <Stack direction="row" spacing={1} sx={{ mt: 0.35, flexWrap: 'wrap' }}>
        {formatCareLibraryItemLastObserved(item) ? (
          <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color: alpha(colors.semantic.ink, 0.64) }}>
            {formatCareLibraryItemLastObserved(item)}
          </Typography>
        ) : null}
        <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color: alpha(colors.semantic.ink, 0.64) }}>
          {formatCareLibraryEvidenceCount(item.evidenceCount || 1)}
        </Typography>
        {item.profileRepresentation?.label ? (
          <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color: alpha(colors.semantic.sageDeep, 0.92) }}>
            {item.profileRepresentation.label}
          </Typography>
        ) : null}
      </Stack>
      {item.summary ? (
        <Typography sx={{ mt: 0.4, fontSize: '0.84rem', color: alpha(colors.semantic.ink, 0.68), lineHeight: 1.35 }}>
          {item.summary}
        </Typography>
      ) : null}
    </Box>
  </ButtonBase>
);

const LibrarySubjectDetailSheet = ({ open = false, subject = null, onClose = () => {}, onOpenItem = () => {} }) => {
  if (!subject) return null;

  return (
    <Drawer
      anchor="bottom"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: '100%',
          maxWidth: '100vw',
          height: 'auto',
          maxHeight: '86vh',
          borderTopLeftRadius: '30px',
          borderTopRightRadius: '30px',
          borderBottomLeftRadius: 0,
          borderBottomRightRadius: 0,
          bgcolor: colors.semantic.profileFlow.careStory.pageBg,
          overflow: 'hidden',
        },
      }}
    >
      <Box sx={{ height: '100%', overflow: 'auto' }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', pt: 1.05 }}>
          <Box sx={{ width: 54, height: 5, borderRadius: 999, bgcolor: colors.semantic.profileFlow.careStory.sheetHandle }} />
        </Box>

        <Box sx={{ px: { xs: 1.55, sm: 2 }, pt: 1.4, pb: 1.35, borderBottom: `1px solid ${colors.semantic.line}` }}>
          <Stack direction="row" spacing={1.2} alignItems="center" justifyContent="space-between">
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontSize: { xs: '1.08rem', sm: '1.22rem' }, fontWeight: 920, color: colors.semantic.profileFlow.careStory.text, lineHeight: 1.08 }}>
                {subject.label}
              </Typography>
              <Typography sx={{ mt: 0.35, fontSize: '0.88rem', color: alpha(colors.semantic.ink, 0.66), lineHeight: 1.4 }}>
                {subject.description}
              </Typography>
            </Box>
            <ButtonBase
              aria-label="Close subject details"
              onClick={onClose}
              sx={{
                width: 46,
                height: 46,
                borderRadius: '50%',
                bgcolor: '#FFFFFF',
                color: colors.semantic.ink,
                boxShadow: `0 4px 14px ${colors.landing.shadowSoft}`,
                '&:hover': { bgcolor: colors.semantic.sageMist },
              }}
            >
              <CloseRoundedIcon />
            </ButtonBase>
          </Stack>
        </Box>

        <Stack spacing={1.05} sx={{ px: { xs: 1.55, sm: 2 }, py: 1.6 }}>
          {subject.items.map((item) => (
            <LibrarySubjectItemRow
              key={item.id}
              item={item}
              onOpen={() => onOpenItem(item)}
            />
          ))}
        </Stack>
      </Box>
    </Drawer>
  );
};

const CareLibrarySection = ({ childId = '', initialViewMode = 'all' }) => {
  const [sources, setSources] = useState([]);
  const [loading, setLoading] = useState(Boolean(childId));
  const [error, setError] = useState('');
  const [viewMode, setViewMode] = useState(TABS.some((tab) => tab.value === initialViewMode) ? initialViewMode : 'all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedSource, setSelectedSource] = useState(null);
  const [selectedSubject, setSelectedSubject] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    setViewMode(TABS.some((tab) => tab.value === initialViewMode) ? initialViewMode : 'all');
  }, [initialViewMode]);

  useEffect(() => {
    if (!childId) {
      setSources([]);
      setLoading(false);
      return undefined;
    }

    setLoading(true);
    setError('');
    return subscribeToCareDocumentSources({
      childId,
      onSources: (nextSources) => {
        setSources(nextSources);
        setLoading(false);
      },
      onError: () => {
        setError('Library could not load right now.');
        setLoading(false);
      },
    });
  }, [childId]);

  const filteredSources = useMemo(() => {
    const query = normalizeText(searchQuery).toLowerCase();
    const tabFiltered = filterLibrarySourcesByTab(sources, viewMode);
    if (!query) return tabFiltered;
    return tabFiltered.filter((source) => (
      getCareLibrarySourceTitle(source).toLowerCase().includes(query)
      || getCareLibrarySourceCollectedRows(source).some((row) => (
        normalizeText(row.title).toLowerCase().includes(query)
        || normalizeText(row.subtitle).toLowerCase().includes(query)
      ))
    ));
  }, [searchQuery, sources, viewMode]);

  const subjectGroups = useMemo(() => buildCareLibrarySubjectGroups(sources), [sources]);
  const filteredSubjectGroups = useMemo(() => {
    const query = normalizeText(searchQuery).toLowerCase();
    if (!query) return subjectGroups;

    return subjectGroups
      .map((group) => {
        const matchingItems = group.items.filter((item) => (
          normalizeText(item.title).toLowerCase().includes(query)
          || normalizeText(item.summary).toLowerCase().includes(query)
          || normalizeText(group.label).toLowerCase().includes(query)
          || normalizeText(group.description).toLowerCase().includes(query)
        ));
        if (!matchingItems.length) return null;
        return {
          ...group,
          itemCount: matchingItems.length,
          items: matchingItems,
        };
      })
      .filter(Boolean);
  }, [searchQuery, subjectGroups]);

  useEffect(() => {
    if (viewMode !== 'bySubject') {
      setSelectedSubject(null);
    }
  }, [viewMode]);

  const openSourceDetails = (source) => setSelectedSource(source);

  const openOriginalSource = (source = {}) => {
    const sourceId = String(source.id || '').trim();
    if (!sourceId) return;
    navigate(`/sources/${sourceId}`);
  };

  const openSourceItem = (item = {}, source = {}) => {
    const destination = buildCareLibrarySubjectItemDestination(item);
    if (destination.type === 'timeline' && destination.entryId) {
      navigate('/dashboard', {
        state: {
          selectedChildId: source.childId || null,
          timelineEntryId: destination.entryId,
        },
      });
      return;
    }

    if (destination.type === 'source' && destination.sourceId) {
      navigate(`/sources/${destination.sourceId}`);
      return;
    }

    setSelectedSource(source);
  };

  const handleDocumentNavigation = (itemId) => navigateFromDocumentLibrary({ navigate, childId, itemId });

  if (loading) {
    return (
      <Paper elevation={0} sx={{ p: 2, borderRadius: '16px', border: `1px solid ${alpha(colors.brand.tint, 0.18)}`, bgcolor: '#FFFFFF' }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <CircularProgress size={20} thickness={4} />
          <Typography sx={{ fontSize: '0.92rem', color: alpha(colors.semantic.ink, 0.68) }}>
            Loading Library...
          </Typography>
        </Stack>
      </Paper>
    );
  }

  if (error) {
    return <Alert severity="warning">{error}</Alert>;
  }

  if (!sources.length) {
    return null;
  }

  const renderSourceList = (items = []) => (
    <Stack spacing={1}>
      {items.map((source) => (
        <LibrarySourceCard
          key={source.id}
          source={source}
          onOpenSource={openOriginalSource}
          onOpenItem={openSourceItem}
        />
      ))}
    </Stack>
  );

  return (
    <Box sx={{ width: '100%' }}>
      {viewMode === 'documents' ? <DesktopCareDocumentLibrary sources={filteredSources} onOpenSource={openOriginalSource} onAddFiles={() => navigate('/library/add')} /> : null}
      {viewMode === 'documents' ? <MobileCareDocumentLibrary sources={filteredSources} onOpenSource={openOriginalSource} onAddFiles={() => navigate('/library/add')} onNavigate={handleDocumentNavigation} /> : null}
      <Stack spacing={1.15} sx={{ display: viewMode === 'documents' ? 'none' : 'flex' }}>
        <Box>
          <Typography sx={{ fontSize: { xs: '1.22rem', sm: '1.38rem' }, fontWeight: 940, color: colors.semantic.profileFlow.careStory.text, lineHeight: 1.06 }}>
            Library
          </Typography>
          <Typography sx={{ mt: 0.35, maxWidth: 640, fontSize: { xs: '0.84rem', sm: '0.9rem' }, color: alpha(colors.semantic.ink, 0.68), lineHeight: 1.4 }}>
            Browse saved care sources by file type, by source record, or by subject.
          </Typography>
        </Box>

        <Tabs
          value={viewMode}
          onChange={(event, nextValue) => setViewMode(nextValue)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            minHeight: 40,
            borderBottom: `1px solid ${alpha(colors.semantic.line, 0.72)}`,
            '& .MuiTab-root': {
              minHeight: 40,
              textTransform: 'none',
              fontSize: '0.92rem',
              fontWeight: 750,
              px: 1.25,
            },
          }}
        >
          {TABS.map((tab) => (
            <Tab key={tab.value} value={tab.value} label={tab.label} />
          ))}
        </Tabs>

        {viewMode === 'documents' ? <ImportHistoryLibraryLink /> : null}

        <TextField
          fullWidth
          value={searchQuery}
          onChange={(event) => setSearchQuery(event.target.value)}
          placeholder="Search Library"
          size="small"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchRoundedIcon sx={{ color: alpha(colors.semantic.ink, 0.68) }} />
              </InputAdornment>
            ),
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              minHeight: 44,
              borderRadius: '14px',
              bgcolor: '#FFFFFF',
              fontSize: '0.86rem',
              color: colors.semantic.ink,
              '& fieldset': { borderColor: colors.semantic.line },
              '&:hover fieldset': { borderColor: colors.semantic.line2 },
              '&.Mui-focused fieldset': { borderColor: colors.semantic.sageDeep },
            },
          }}
        />

        {!filteredSources.length ? (
          <Typography sx={{ fontSize: '0.92rem', color: alpha(colors.semantic.ink, 0.66) }}>
            No matching Library sources.
          </Typography>
        ) : viewMode === 'bySubject' ? (
          filteredSubjectGroups.length ? (
            <Stack spacing={1.1}>
              {filteredSubjectGroups.map((group) => (
                <LibrarySubjectSummaryCard key={group.id} group={group} onOpen={() => setSelectedSubject(group)} />
              ))}
            </Stack>
          ) : (
            <Typography sx={{ fontSize: '0.92rem', color: alpha(colors.semantic.ink, 0.66) }}>
              No matching subjects.
            </Typography>
          )
        ) : (
          renderSourceList(filteredSources)
        )}
      </Stack>

      <CareDocumentDetailSheet
        open={Boolean(selectedSource)}
        source={selectedSource}
        onClose={() => setSelectedSource(null)}
      />

      <LibrarySubjectDetailSheet
        open={Boolean(selectedSubject)}
        subject={selectedSubject}
        onClose={() => setSelectedSubject(null)}
        onOpenItem={openSourceItem}
      />
    </Box>
  );
};

export default CareLibrarySection;
